import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { CHAT } from "@/constants/testIds";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WELCOME = {
  role: "assistant",
  content: "Hi, I'm Grace — your guide at Faith Family Finance. Ask me anything about life insurance, or tell me a bit about your family and I'll help you figure out which type of policy fits best.",
};

function newSessionId() {
  return `ffp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [sessionId] = useState(newSessionId);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/intake")) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data:")) continue;
          const payload = trimmed.slice(5).trim();
          if (!payload) continue;
          try {
            const data = JSON.parse(payload);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, content: (last.content || "") + data.delta };
                }
                return copy;
              });
            }
            if (data.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble responding right now. Please try again in a moment, or use the quote form and we'll reach out personally." };
                return copy;
              });
            }
          } catch (_) {
            // ignore malformed SSE chunk
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = { role: "assistant", content: "Sorry — connection hiccup. Please try again." };
        }
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        data-testid={CHAT.fab}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open chat"
        style={{ bottom: "72px" }}
        className="fixed right-6 z-[60] h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105 flex items-center justify-center ring-4 ring-background"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>

      {open && (
        <div
          data-testid={CHAT.panel}
          style={{ bottom: "144px" }}
          className="fixed right-6 z-[60] w-[min(380px,calc(100vw-3rem))] h-[560px] max-h-[calc(100vh-12rem)] rounded-3xl bg-background border border-border/60 shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="px-5 py-4 border-b border-border/60 bg-card flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
              <span className="text-primary-foreground text-sm font-serif italic">G</span>
            </div>
            <div className="flex-1">
              <p className="font-serif text-lg text-primary leading-tight">Grace</p>
              <p className="text-xs text-muted-foreground">Faith Family Finance assistant</p>
            </div>
            <button
              data-testid={CHAT.close}
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground p-1"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-testid={CHAT.messages}
            className="flex-1 overflow-y-auto p-5 space-y-4 bg-muted/30"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={CHAT.message(i)}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-card text-foreground border border-border/60 rounded-bl-md"
                  }`}
                >
                  {m.content || (sending && i === messages.length - 1 ? (
                    <span className="inline-flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" /> thinking…
                    </span>
                  ) : null)}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-border/60 bg-background">
            <div className="flex items-end gap-2">
              <textarea
                data-testid={CHAT.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about coverage, premiums, beneficiaries…"
                rows={1}
                className="flex-1 resize-none rounded-2xl border border-border/60 bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 max-h-32"
              />
              <Button
                data-testid={CHAT.send}
                onClick={send}
                disabled={sending || !input.trim()}
                size="icon"
                className="h-10 w-10 rounded-full bg-primary hover:bg-primary/90 shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
